import { useEffect, useRef, useState } from 'react';
import { api, ApiError } from '../api/client';
import type { AiJob, AiStatus, AnalysisResult, FindingKind } from '../api/types';
import { formatDateTime, kindLabel, krw } from '../lib/format';
import { useAsync } from '../lib/useAsync';
import { useApp } from '../state';

const FINDING_LABELS: Record<FindingKind, string> = {
  category_change: '분류 변화',
  top_merchant: '자주 쓴 곳',
  fixed_cost: '고정비',
  missing_recurring: '누락 가능성',
  duplicate_candidate: '중복 후보',
  budget_over: '예산 초과',
  concentration: '지출 집중',
  income_shortfall: '수입 대비 지출',
  limited_data: '근거 제한',
  other: '참고'
};
const CONFIDENCE: Record<string, string> = { low: '확신 낮음', medium: '확신 보통', high: '확신 높음' };
const BASIS: Record<AnalysisResult['comparison']['basis'], string> = {
  previous_month_same_days: '전월 같은 기간',
  previous_year: '직전 연도',
  previous_period: '직전 같은 일수'
};

/**
 * AI 분석(설계서 8장): 버튼 → 작업 등록(202) → worker 실행 → 2초 간격 확인, 30초 뒤 5초 간격.
 * 금액·비율은 서버가 계산한 facts를 그대로 보여주고, AI 문장은 설명으로만 붙인다. 분석은 기록을 바꾸지 않는다.
 */
export function InsightsPage() {
  const { period, dataVersion, navigate } = useApp();
  const status = useAsync(() => api.get<{ data: AiStatus }>('/ai/status').then((r) => r.data), [dataVersion]);
  const [job, setJob] = useState<AiJob | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [requesting, setRequesting] = useState(false);
  const generation = useRef(0);
  const poll = useRef<{ timer?: number; started: number; id?: string }>({ started: 0 });

  // 기간을 바꾸거나 화면을 다시 열면 그 기간의 마지막 분석을 보여준다.
  useEffect(() => {
    const current = ++generation.current;
    setRequesting(false);
    setJob(null);
    setError(null);
    api.get<{ data: AiJob[] }>('/ai/jobs').then(
      (r) => {
        if (current !== generation.current) return;
        const latest = r.data.find((j) => j.period.from === period.start && j.period.to === period.end);
        if (latest) track(latest, current);
      },
      () => undefined
    );
    return () => {
      generation.current += 1;
      stopPolling();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period.start, period.end, dataVersion]);

  function stopPolling() {
    clearTimeout(poll.current.timer);
    poll.current.id = undefined;
  }

  function track(next: AiJob, current: number) {
    if (current !== generation.current) return;
    setJob(next);
    if (next.status === 'succeeded' || next.status === 'failed') return stopPolling();
    if (poll.current.id !== next.id) poll.current = { started: Date.now(), id: next.id };
    const delay = Date.now() - poll.current.started < 30_000 ? 2_000 : 5_000;
    clearTimeout(poll.current.timer);
    poll.current.timer = window.setTimeout(async () => {
      if (current !== generation.current || poll.current.id !== next.id) return;
      try {
        track((await api.get<{ data: AiJob }>(`/ai/jobs/${next.id}`)).data, current);
      } catch (e) {
        if (current !== generation.current || poll.current.id !== next.id) return;
        // 연결이 잠깐 끊겨도 작업은 서버에서 계속된다. 조금 뒤 다시 확인한다.
        if (e instanceof ApiError && e.status === 404) return stopPolling();
        poll.current.timer = window.setTimeout(() => track(next, current), 5_000);
      }
    }, delay);
  }

  async function analyze() {
    const current = ++generation.current;
    stopPolling();
    setRequesting(true);
    setError(null);
    try {
      const res = await api.post<{ data: AiJob }>('/ai/analysis', { from: period.start, to: period.end });
      if (current !== generation.current) return;
      track(res.data, current);
      status.reload();
    } catch (e) {
      if (current !== generation.current) return;
      setError(e instanceof ApiError ? e.message : '분석을 요청하지 못했습니다.');
    } finally {
      if (current === generation.current) setRequesting(false);
    }
  }

  const enabled = status.data?.enabled;
  const running = job?.status === 'queued' || job?.status === 'running';
  const result = job?.status === 'succeeded' ? job.result : null;

  return (
    <section>
      <article className="analysis-hero">
        <div>
          <span className="eyebrow">A MOMENT TO REFLECT</span>
          <h2>소비를 돌아보는 시간</h2>
          <p>
            {period.start} ~ {period.end} 기록에서 패턴을 찾고, 확인할 부분을 모아드려요.
          </p>
        </div>
        <button className="primary" onClick={() => void analyze()} disabled={!enabled || requesting || running}>
          {running ? '분석 중…' : enabled === false ? '✧ AI 분석 (꺼짐)' : result && !job?.stale ? '✧ 결과 다시 보기' : '✧ AI 분석'}
        </button>
      </article>
      <p className="notice">
        합계·비교·누락·중복 후보는 서버가 먼저 계산하고, AI는 그 결과를 설명만 합니다. AI에는 기간 집계와 큰 지출·중복 후보 거래의 날짜·금액·사용처·분류만 보내며 메모와 계좌 정보는 보내지 않습니다. 금융기관 내역 연동이 없어 실제 누락을 확정할 수는 없습니다.
      </p>
      {enabled === false && <p className="notice warn">서버에 AI 키가 설정되지 않아 분석을 쓸 수 없습니다. 대시보드에서 기간별 합계를 확인해주세요.</p>}
      {error && (
        <p className="notice warn" role="alert">
          {error}
        </p>
      )}
      <div aria-live="polite">
        {running && <div className="empty">AI가 기록을 살펴보고 있어요… 보통 10초 안에 끝나요. 이 화면을 떠나도 분석은 계속됩니다.</div>}
        {job?.status === 'failed' && (
          <p className="notice warn" role="alert">
            {job.error?.message} <button className="text" onClick={() => void analyze()}>다시 시도</button>
          </p>
        )}
        {result && <AnalysisView result={result} stale={job!.stale} onShowHistory={() => navigate('history')} />}
        {!job && !running && enabled && <div className="empty">분석 버튼을 누르면 근거와 함께 확인 후보를 보여드려요.</div>}
      </div>
    </section>
  );
}

function change(value: string) {
  const n = BigInt(value);
  return n === 0n ? '변화 없음' : `${n > 0n ? '▲' : '▼'} ${krw(n < 0n ? -n : n)}`;
}

function AnalysisView({ result, stale, onShowHistory }: { result: AnalysisResult; stale: boolean; onShowHistory: () => void }) {
  const { facts, ai } = result;
  const txById = new Map(result.evidence.transactions.map((t) => [t.id, t]));
  const occById = new Map(facts.missingRecurring.map((o) => [o.occurrenceId, o]));
  return (
    <>
      {stale && <p className="notice warn">기록 또는 분석 기준일이 바뀌었어요. 아래 결과는 {formatDateTime(result.generatedAt)} 기준입니다. 다시 분석하면 반영됩니다.</p>}
      <article className="card insight-summary">
        <span className="tag">AI 요약</span>
        <p>{ai.summary}</p>
        <small className="hint">
          {formatDateTime(result.generatedAt)} · {result.model} · 분석 기간: {result.period.from} ~ {result.period.to} · 비교: {BASIS[result.comparison.basis]}({result.comparison.from} ~ {result.comparison.to})
          {facts.limitedData && ' · 기록이 적어 근거가 제한됩니다'}
          {facts.detailsTruncated && ' · 세부 후보는 일부만 표시합니다. 합계는 전체 기간 기준입니다.'}
        </small>
      </article>

      <div className="metrics three">
        <Metric label="순지출" value={facts.current.netExpenseKrw} previous={facts.previous.netExpenseKrw} />
        <Metric label="수입" value={facts.current.incomeKrw} previous={facts.previous.incomeKrw} />
        <Metric label="수지(수입 − 순지출)" value={facts.current.balanceKrw} previous={facts.previous.balanceKrw} />
      </div>

      {ai.findings.length > 0 && (
        <div className="insight-list">
          {ai.findings.map((f, i) => (
            <article key={i} className="card insight">
              <span className="tag">{FINDING_LABELS[f.kind]}</span> <small className="muted">{CONFIDENCE[f.confidence]}</small>
              <h2>{f.title}</h2>
              <p>{f.detail}</p>
              {(f.evidenceTransactionIds.length > 0 || f.evidenceOccurrenceIds.length > 0) && (
                <ul className="evidence">
                  {f.evidenceTransactionIds.map((id) => {
                    const t = txById.get(id);
                    return t ? (
                      <li key={id}>
                        {t.effectiveDate} · {t.merchant ?? kindLabel[t.kind]} · {t.categoryName ?? '-'} › {t.subcategoryName ?? '-'} · <b>{krw(t.amountKrw)}</b>
                      </li>
                    ) : null;
                  })}
                  {f.evidenceOccurrenceIds.map((id) => {
                    const o = occById.get(id);
                    return o ? (
                      <li key={id}>
                        예정 {o.scheduledDate} · {o.ruleName} · <b>{o.expectedAmountKrw ? krw(o.expectedAmountKrw) : '변동 금액'}</b>
                      </li>
                    ) : null;
                  })}
                </ul>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="columns">
        <article className="card">
          <div className="card-head">
            <h2>분류별 순지출</h2>
            <span className="muted">비교: {BASIS[result.comparison.basis]}</span>
          </div>
          {facts.categories.length === 0 ? (
            <p className="muted">이 기간에 지출 기록이 없어요.</p>
          ) : (
            <table className="facts">
              <thead>
                <tr>
                  <th>분류</th>
                  <th>이번</th>
                  <th>비중</th>
                  <th>이전 대비</th>
                </tr>
              </thead>
              <tbody>
                {facts.categories.map((c) => (
                  <tr key={c.categoryId ?? 'none'}>
                    <td>{c.name}</td>
                    <td>{krw(c.netKrw)}</td>
                    <td>{c.sharePercent ? `${c.sharePercent}%` : '-'}</td>
                    <td className={BigInt(c.changeKrw) > 0n ? 'negative' : ''}>{change(c.changeKrw)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
          <p className="hint">
            활성 정기 지출의 월 환산 {krw(facts.fixedCost.monthlyEquivalentKrw)}
            {facts.fixedCost.sharePercent && ` · 월 평균 순지출의 ${facts.fixedCost.sharePercent}%`}
          </p>
        </article>
        <article className="card">
          <h2>확인할 항목</h2>
          <h3 className="facts-title">예산 초과</h3>
          {facts.budgetOver.length ? (
            facts.budgetOver.map((b) => (
              <p key={b.categoryId ?? 'total'} className="facts-line">
                {b.name}: 예산 {krw(b.budgetKrw)}{b.partial && '(기간 환산)'} 대비 <b className="negative">{krw(b.overKrw)} 초과</b>
              </p>
            ))
          ) : (
            <p className="muted">없음</p>
          )}
          <h3 className="facts-title">누락 가능성 (예정일이 지났는데 확정 안 된 반복 항목)</h3>
          {facts.missingRecurring.length ? (
            facts.missingRecurring.map((o) => (
              <p key={o.occurrenceId} className="facts-line">
                {o.scheduledDate} · {o.ruleName} · {o.expectedAmountKrw ? krw(o.expectedAmountKrw) : '변동 금액'}
              </p>
            ))
          ) : (
            <p className="muted">없음</p>
          )}
          <h3 className="facts-title">중복 후보 (날짜·금액·사용처가 같은 기록)</h3>
          {facts.duplicateCandidates.length ? (
            <>
              {facts.duplicateCandidates.map((d) => (
                <p key={d.transactionIds.join()} className="facts-line">
                  {d.effectiveDate} · {d.merchant ?? '사용처 없음'} · {krw(d.amountKrw)} × {d.transactionIds.length}건
                </p>
              ))}
              <button className="text" onClick={onShowHistory}>
                전체 내역에서 확인하기 →
              </button>
            </>
          ) : (
            <p className="muted">없음</p>
          )}
          <h3 className="facts-title">자주 쓴 곳</h3>
          {facts.topMerchants.length ? (
            facts.topMerchants.map((m) => (
              <p key={m.merchant} className="facts-line">
                {m.merchant} · {m.count}건 · {krw(m.netKrw)}
              </p>
            ))
          ) : (
            <p className="muted">없음</p>
          )}
        </article>
      </div>

      {ai.suggestedActions.length > 0 && (
        <article className="card insight-actions">
          <h2>해볼 만한 것</h2>
          <ul>
            {ai.suggestedActions.map((a, i) => (
              <li key={i}>{a}</li>
            ))}
          </ul>
          <p className="hint">AI는 기록을 저장·삭제하지 않습니다. 필요한 변경은 직접 해주세요.</p>
        </article>
      )}
    </>
  );
}

function Metric({ label, value, previous }: { label: string; value: string; previous: string }) {
  return (
    <div className="metric">
      <span>{label}</span>
      <strong>{krw(value)}</strong>
      <small>이전 {krw(previous)} · {change(String(BigInt(value) - BigInt(previous)))}</small>
    </div>
  );
}
